import { Component, OnInit, ViewChild } from '@angular/core';
import { ChartService } from '../../services/chart.service';
import { ImportsModule } from '../../imports';
import { UIChart } from 'primeng/chart';
import { TreeNode } from 'primeng/api';
import { Column } from '../Column';
import { DetailTableComponent } from "../../shared/components/detail-table/detail-table.component";
import { DetailTableData } from '../../shared/types';
import { formatNumber } from '@angular/common';


interface ChartData {
  datasets: {
    data: number[],
    borderColor: string,
    backgroundColor?: string,
    label: string | null
  }[],
  labels: string[] 
}


@Component({
  selector: 'app-inventory',
  standalone: true,
  imports: [
    ImportsModule,
    DetailTableComponent
  ],
  templateUrl: './inventory.component.html',
  styleUrl: './inventory.component.css'
})
export class InventoryComponent implements OnInit { 
  @ViewChild('chart') chart!: UIChart;
  @ViewChild('locChart') locChart!: UIChart;
  treeTableResults: TreeNode[] = [];
  inventoryTreeTableResults: TreeNode[] = [];
  results: ChartData = {
    labels: [],
    datasets: [{
      data: [],
      borderColor: "#186082",
      backgroundColor: "#186082",
      label: 'Total Objects'
    },
    {
      data: [],
      borderColor: "#83CBEB",
      backgroundColor: "#83CBEB",
      label: 'Has Source'
    },
    {
      data: [],
      borderColor: "#AB6134",
      backgroundColor: "#AB6134",
      label: 'Missing Source'
    }]
  }
  locResults: ChartData = {
    labels: [],
    datasets: [{
      data: [],
      borderColor: "#186082",
      backgroundColor: "#186082",
      label: null
    }]
  }
  showDetail = false;
  columns: Column[] = [];
  inventoryColumns: Column[] = [];
  inventoryDetailColumns: Column[] = [];

  inventoryDetailResults: {
    [key: string]: string,
  }[] = [];
  detailTableData: DetailTableData = {} as DetailTableData;
  globalFilterFields: string[] = [];
  filteredDetailTableResults: any[] = [];
  selectedValues: { [key: string]: any[] } = {};
  selectedMatchModes: { [key: string]: string } = {};

  scopeOptions: any[] = [
    { label: 'In Scope', value: 'In Scope' },
    { label: 'Out of Scope', value: 'Out of Scope' }
  ];
  scopeValue: any[] = [];

  phaseOptions: {
    label: string, value: number
  }[] = [];
  phaseValue: any[] = [];
  disabledPhase = false;

  options: any;
  typesOptions: any;
  locOptions: any;
  constructor(
    private chartService: ChartService
  ) { }

  ngOnInit() {
    this.scopeValue = this.scopeOptions.map((item: any) => item.value);
    const scalesOptions = {
      y: {
        ticks: {
          display: false
        },
        grid: {
          display: false
        }
      }
    }
    this.typesOptions = {
      scales: scalesOptions,
      maxBarThickness: 50,
      plugins: {
        title: {
          display: true,
          align: 'start',
          color: '#186182',
          font: {
            size: 16
          },
          text: "Objects per Object Type (In Scope)"
        },
        datalabels: {
          display: true,
          color: 'black',
          align: 'end',
          anchor: 'end',
          clamp: true,
          formatter: function(value: any) {
            return formatNumber(value, 'en-US', '1.0-0');
          }
        }
      }
    }

    this.locOptions = {
      scales: scalesOptions,
      maxBarThickness: 50,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          align: 'start',
          color: '#186182',
          font: {
            size: 16
          },
          text: "Lines of Code per Object Type (In Scope)"
        },
        datalabels: {
          display: true,
          color: 'black',
          align: 'end',
          anchor: 'end',
          clamp: true,
          formatter: function(value: any) {
            return formatNumber(value, 'en-US', '1.0-0');
          }
        }
      }
    }
    
    this.columns = [
      { label: 'Library', field: 'library', width: 175 },
      { label: 'File', field: 'file', width: 70 },
      { label: "Member", field: "member", width: 70 },
      { label: "Copybooks", field: "copybooks", width: 45, align: 'right' },
      { label: "Copybooks Used", field: "copybookused", width: 75, align: 'right' },
    ];

    this.inventoryColumns = [
      { label: 'Object Type', field: 'object_type', width: 120 },
      { label: 'Attribute', field: 'attribute', width: 120 },
      { label: 'Total Objects', field: 'total_objects', width: 120, align: 'right' },
      { label: 'Lines of Code', field: 'loc', width: 120, align: 'right' },
    ]

    this.inventoryDetailColumns = [
      { label: 'Scope Phase', field: 'scopedesc', width: 120, filterType: 'select' },
      { label: 'Library', field: 'library', width: 175, filterType: 'text', matchMode: "contains"  },
      { label: 'Name', field: 'name', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Type', field: 'type', width: 120, filterType: 'select' },
      { label: 'Attribute', field: 'attribute', width: 120, filterType: 'select' },
      { label: 'Text', field: 'text', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Source Lines', field: 'srcLines', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Source Match', field: 'srcMatch', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Has Source', field: 'hasSource', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Target', field: 'target', width: 120, filterType: 'select' },
      { label: 'Resolved Scope', field: 'resolvedScope', width: 120, filterType: 'select' },
      { label: 'Reason', field: 'reason', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Notes', field: 'notes', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'App Area ID', field: 'appAreaId', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'App Area Description', field: 'appAreaDescription', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Created', field: 'created', width: 175, filterType: 'date' },
      { label: 'Changed', field: 'changed', width: 175, filterType: 'date' },
      { label: 'Last Used', field: 'lastUsed', width: 175, filterType: 'date' },
      { label: 'Restored', field: 'restored', width: 175, filterType: 'date' }
    ]

    this.chartService.getScopePhases().subscribe({
      next: (data: any) => {
        if(data.rowsAffected > 0){
          this.phaseOptions = data.rows.map((item: any) => {
            return {
              label: item.description,
              value: item.phase
            }
          });
        }
        this.phaseValue = this.phaseOptions.map((item: any) => item.value);
      },
      complete: () => {
        this.resetCharts();
        this.resetTables();
      }
    })
  }

  resetCharts(){
    this.chartService.getSourceAnalysisChart(['In Scope'], null).subscribe({
    // this.chartService.getSourceAnalysisChart(this.scopeValue, this.phaseValue).subscribe({
      next: (data: any) => {
        this.cleanChartsData('left');
        data.forEach((item: any, index: number) => {
          this.results.datasets[0].data.push(item.total);
          this.results.datasets[1].data.push(item.trueCount);
          this.results.datasets[2].data.push(item.falseCount);
          this.results.labels.push(item.object_type);
        });
        this.chart.reinit();
      },
      complete: () => {
        this.disabledPhase = false;
      }
    });

    this.chartService.getLOCChart(['In Scope'], null).subscribe({
    // this.chartService.getLOCChart(this.scopeValue, this.phaseValue).subscribe({
      next: (data: any) => {
        if(data.rowsAffected > 0){
          this.cleanChartsData('right');
          data.rows.forEach((item: any, index: number) => {
            this.locResults.datasets[0].data.push(item.loc);
            this.locResults.labels.push(item.object_type);
          });

        }
        this.locChart.reinit();
      },
      complete: () => {
        this.disabledPhase = false;
      }
    });
  }

  cleanChartsData(chart: string){
    switch(chart){
      case 'left':
        this.results.datasets[0].data = [];
        this.results.datasets[1].data = [];
        this.results.datasets[2].data = [];
        this.results.labels = [];
        break;
      case 'right':
        this.locResults.datasets[0].data = [];
        this.locResults.labels = [];
        break;
      default:
        return;
    }
  }

  resetTables(){
    this.chartService.getCopybookSummary().subscribe({
      next: (data: any) => {
        if(data.rowsAffected > 0){
          this.treeTableResults = data.rows.map((item: any) => {
            return {
              data: {
                library: item.library,
                file: "",
                member: "",
                copybooks: item.copybooks,
                copybookused: item.copybooksused
              }
            }
          });
        }
      },
      complete: () => {
        this.treeTableResults.forEach((item: TreeNode, index: number) => {
          this.chartService.getCopybookByLibrary(item.data.library).subscribe({
            next: (data: any) => {
              if(data.rowsAffected > 0){
                item.children = data.rows.map((row: any) => {
                  return {
                    data: {
                      library: row.library,
                      file: row.file,
                      member: row.member,
                      copybooks: row.copybooks,
                      copybookused: row.copybooksused
                    },
                    children: []
                  }
                });
              }
            },
            complete: () => {
              this.treeTableResults.forEach((item: TreeNode, index: number) => {
                if(item.children){
                  item.children.forEach((child: TreeNode, index: number) => {
                    this.chartService.getCopybookByFile(item.data.library, child.data.file).subscribe({
                      next: (data: any) => {
                        if(data.rowsAffected > 0){
                          child.children = data.rows.map((row: any) => {
                            return {
                              data: {
                                library: "",
                                file: "",
                                member: row.member,
                                copybooks: row.copybooks,
                                copybookused: row.copybooksused
                              }
                            }
                          });
                        }
                      }
                    });
                  });
                }
              });
            }
          });
        })
      }
    })

    this.chartService.getInventorySummary(this.scopeValue, this.phaseValue).subscribe({
      next: (data: any) => {
        if(data.rowsAffected > 0){
          this.inventoryTreeTableResults = data.rows.map((item: any) => {
            return {
              data: {
                object_type: item.object_type,
                attribute: '',
                library: '',
                library_name: '',
                source_available: '',
                total_objects: item.total_objects,
                loc: item.total_loc
              }
            }
          });
        }
      },
      complete: () => {
        this.inventoryTreeTableResults.forEach((item: TreeNode, index: number) => {
          this.chartService.getInventotyByType(item.data.object_type, this.scopeValue, this.phaseValue).subscribe({
            next: (data: any) => {
              if(data.rowsAffected > 0){
                item.children = data.rows.map((row: any) => {
                  return {
                    data: {
                      object_type: item.data.object_type,
                      attribute: row.object_attribute,
                      library: '',
                      source_available: '',
                      total_objects: row.total_objects,
                      loc: row.loc
                    },
                    leaf: true
                  }
                });
              }
            }
          });
        });
      }
    });
  }

  backToSummary() {
    this.showDetail = false;
    this.filteredDetailTableResults = [];
    this.inventoryDetailResults = [];
  }

  showDetailedTable(col: any, rowNode: any) {
    const { object_type, attribute } = rowNode.node.data;
    if(col === 'attribute' && rowNode.node.leaf === true){
      this.chartService.getInventoryDetail(object_type, attribute, this.scopeValue, this.phaseValue).subscribe({
        next: (data: any) => {
          if(data.rowsAffected > 0){
            this.inventoryDetailResults = data.rows.map((item: any) => {
              return {
                scopedesc: item.scopedesc == null ? 'None' : item.scopedesc,
                library: item.object_library,
                name: item.object_name,
                type: item.object_type,
                attribute: item.object_attribute,
                text: item.object_text,
                created: item.object_created,
                changed: item.object_changed,
                lastUsed: item.object_lastused,
                restored: item.object_restored,
                srcLines: item.object_srclines,
                srcMatch: item.object_srcmatch,
                hasSource: item.hassource,
                target: item.object_target,
                resolvedScope: item.resolved_scope,
                reason: item.object_reason,
                notes: item.object_notes,
                appAreaId: item.apparea_areaaid,
                appAreaDescription: item.apparea_description,
              }
            });
            this.filteredDetailTableResults = this.inventoryDetailResults;
            this.detailTableData = {
              items: this.inventoryDetailResults,
              columns: this.inventoryDetailColumns,
              globalFilterFields: this.globalFilterFields,
              detailTableResults: this.inventoryDetailResults
            }
            this.showDetail = true;
          }
        }
      });
    }    
  }

  isNumber(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }

  onScopeChange(event: any) {
    if(this.scopeValue.length == 0) {
      this.scopeValue[0] = event.option.value;
      return;
    }
    this.resetTables();
    // this.resetCharts();
    return true
  }

  onPhaseChange(event: any) {
    if(this.phaseValue.length == 0) {
      this.phaseValue[0] = event.option.value;
      return;
    }
    this.resetTables();
    // this.resetCharts();
    return true
  }
}
